import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ColorPicker } from './ColorPicker';
import { FontFamilySelector } from './FontSelector';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Bold, Italic, Underline, AlignLeft, AlignCenter, AlignRight, 
  Link, Image, List, ListOrdered, Type, Palette, PanelLeft, 
  Save, Heading, Undo, Redo, Copy, Trash2, 
  Layout, Smartphone, Monitor, FileImage, Grid, LayoutTemplate, 
  Terminal, Settings, ChevronsUpDown, Eye, ChevronLeft, ChevronRight
} from 'lucide-react';
import { EmailEditor } from './components/EmailEditor';
import { TemplateGallery } from './components/TemplateGallery';
import { EmailSettings } from './components/EmailSettings';
import { nanoid } from 'nanoid';

// For now, this is a mock component that will be expanded in the future
// A full implementation would require a more complex HTML editor or a template builder

interface VisualEditorProps {
  initialContent: string;
  onContentChange: (content: string) => void;
  subject?: string;
  onSubjectChange?: (subject: string) => void;
  type: 'email' | 'sms' | 'whatsapp';
}

export default function VisualEditor({ 
  initialContent, 
  onContentChange,
  subject,
  onSubjectChange,
  type
}: VisualEditorProps) {
  const [activeTab, setActiveTab] = useState<string>('content');
  const [currentSubject, setCurrentSubject] = useState(subject || '');
  const [message, setMessage] = useState('');
  const [editorContent, setEditorContent] = useState(initialContent);
  const [showTemplateGallery, setShowTemplateGallery] = useState(false);
  const [showUnsavedChangesWarning, setShowUnsavedChangesWarning] = useState(false);
  
  // Update subject when prop changes
  useEffect(() => {
    if (subject !== undefined) {
      setCurrentSubject(subject);
    }
  }, [subject]);
  
  // Update content when prop changes
  useEffect(() => {
    if (initialContent !== editorContent && initialContent) {
      setEditorContent(initialContent);
      setShowUnsavedChangesWarning(false);
    }
  }, [initialContent]);
  
  const handleSubjectChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSubject = e.target.value;
    setCurrentSubject(newSubject);
    if (onSubjectChange) {
      onSubjectChange(newSubject);
    }
  };
  
  const handleContentUpdate = (content: string) => {
    setEditorContent(content);
    setShowUnsavedChangesWarning(true);
  };
  
  const handleSave = () => {
    onContentChange(editorContent);
    if (onSubjectChange && type === 'email') {
      onSubjectChange(currentSubject);
    }
    setShowUnsavedChangesWarning(false);
    
    // Show success message
    setMessage('Changes saved successfully!');
    setTimeout(() => {
      setMessage('');
    }, 3000);
  };
  
  const handleTemplateSelect = (template: any) => {
    // Prompt for unsaved changes if needed
    if (showUnsavedChangesWarning) {
      if (!window.confirm("You have unsaved changes. Are you sure you want to switch templates?")) {
        return;
      }
    }
    
    setEditorContent(template.htmlContent);
    if (type === 'email' && onSubjectChange) {
      setCurrentSubject(template.subject);
      onSubjectChange(template.subject);
    }
    setShowTemplateGallery(false);
    setShowUnsavedChangesWarning(true);
  };
  
  const insertVariable = (variable: string) => {
    // This would be implemented to insert variables at cursor position
    // For now, we'll just show a message
    setMessage(`Variable ${variable} would be inserted at cursor position`);
    setTimeout(() => {
      setMessage('');
    }, 3000);
  };
  
  return (
    <div className="bg-white rounded-lg border shadow-sm flex flex-col h-full">
      <div className="border-b p-3 flex items-center justify-between bg-muted/30">
        <h3 className="font-medium">Visual Editor</h3>
        <div className="flex items-center gap-2">
          {showUnsavedChangesWarning && (
            <div className="text-sm text-amber-600">Unsaved changes</div>
          )}
          <Button size="sm" onClick={handleSave} className="gap-1">
            <Save className="h-4 w-4" />
            Save Changes
          </Button>
        </div>
      </div>
      
      {type === 'email' && (
        <div className="p-4 border-b">
          <Label htmlFor="subject">Subject</Label>
          <Input 
            id="subject" 
            value={currentSubject} 
            onChange={handleSubjectChange} 
            className="mt-1"
            placeholder="Enter email subject..."
          />
        </div>
      )}
      
      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
        <div className="border-b px-4">
          <TabsList className="h-10">
            <TabsTrigger value="content" className="data-[state=active]:bg-background">Content</TabsTrigger>
            <TabsTrigger value="design" className="data-[state=active]:bg-background">Design</TabsTrigger>
            <TabsTrigger value="settings" className="data-[state=active]:bg-background">Settings</TabsTrigger>
          </TabsList>
        </div>
        
        <div className="flex-1 overflow-hidden">
          {/* Content Tab */}
          <TabsContent value="content" className="h-full data-[state=inactive]:hidden">
            {showTemplateGallery ? (
              <div className="p-4 h-full flex flex-col">
                <div className="flex justify-between items-center mb-4">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setShowTemplateGallery(false)}
                    className="gap-1"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Back to Editor
                  </Button>
                  <h3 className="font-medium">Choose a Template</h3>
                  <div className="w-[100px]"></div> {/* Spacer for alignment */}
                </div>
                <TemplateGallery onSelectTemplate={handleTemplateSelect} />
              </div>
            ) : (
              <div className="h-full flex flex-col">
                <div className="p-2 bg-muted/20 border-b flex items-center justify-between">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setShowTemplateGallery(true)}
                    className="gap-1"
                  >
                    <LayoutTemplate className="h-4 w-4" />
                    Templates
                  </Button>
                  
                  {message && (
                    <div className="bg-green-100 text-green-800 text-sm px-3 py-1 rounded-md">
                      {message}
                    </div>
                  )}
                </div>
                
                <div className="flex-1 overflow-hidden">
                  <EmailEditor 
                    initialContent={editorContent}
                    onSave={handleContentUpdate}
                  />
                </div>
              </div>
            )}
          </TabsContent>
          
          {/* Design Tab */}
          <TabsContent value="design" className="p-6 h-full overflow-auto data-[state=inactive]:hidden">
            <div className="grid md:grid-cols-2 gap-8">
              <Card className="p-6">
                <h3 className="font-medium mb-4">Color Scheme</h3>
                <div className="space-y-4">
                  <div>
                    <Label>Primary Color</Label>
                    <ColorPicker 
                      color="#4F46E5" 
                      onChange={() => {}} 
                    />
                  </div>
                  
                  <div>
                    <Label>Background Color</Label>
                    <ColorPicker 
                      color="#FFFFFF" 
                      onChange={() => {}} 
                    />
                  </div>
                  
                  <div>
                    <Label>Text Color</Label>
                    <ColorPicker 
                      color="#333333" 
                      onChange={() => {}} 
                    />
                  </div>
                  
                  <div>
                    <Label>Accent Color</Label>
                    <ColorPicker 
                      color="#F59E0B" 
                      onChange={() => {}} 
                    />
                  </div>
                </div>
              </Card>
              
              <Card className="p-6">
                <h3 className="font-medium mb-4">Typography</h3>
                <div className="space-y-4">
                  <div>
                    <Label>Heading Font</Label>
                    <FontFamilySelector 
                      value="Arial, sans-serif" 
                      onChange={() => {}} 
                    />
                  </div>
                  
                  <div>
                    <Label>Body Font</Label>
                    <FontFamilySelector 
                      value="Arial, sans-serif" 
                      onChange={() => {}} 
                    />
                  </div>
                  
                  <div>
                    <Label>Default Font Size</Label>
                    <Input type="number" defaultValue="16" className="mt-1" />
                  </div>
                </div>
              </Card>
              
              <Card className="p-6">
                <h3 className="font-medium mb-4">Layout</h3>
                <div className="space-y-4">
                  <div>
                    <Label>Content Width</Label>
                    <Input type="number" defaultValue="600" className="mt-1" />
                  </div>
                  
                  <div>
                    <Label>Content Padding</Label>
                    <Input type="number" defaultValue="20" className="mt-1" />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <Button variant="outline" className="w-full gap-1">
                      <Layout className="h-4 w-4" />
                      Single Column
                    </Button>
                    <Button variant="outline" className="w-full gap-1">
                      <Grid className="h-4 w-4" />
                      Two Columns
                    </Button>
                  </div>
                </div>
              </Card>
              
              <Card className="p-6">
                <h3 className="font-medium mb-4">Header & Footer</h3>
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <div className="flex-1">
                      <Label>Show Header</Label>
                    </div>
                    <Button variant="outline" size="sm">Customize</Button>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <div className="flex-1">
                      <Label>Show Footer</Label>
                    </div>
                    <Button variant="outline" size="sm">Customize</Button>
                  </div>
                  
                  <div>
                    <Label>Include Social Links</Label>
                    <div className="grid grid-cols-3 gap-2 mt-2">
                      <Button variant="outline" size="sm">Facebook</Button>
                      <Button variant="outline" size="sm">Twitter</Button>
                      <Button variant="outline" size="sm">Instagram</Button>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          </TabsContent>
          
          {/* Settings Tab */}
          <TabsContent value="settings" className="h-full overflow-auto data-[state=inactive]:hidden">
            <EmailSettings 
              htmlContent={editorContent}
              onVariableInsert={insertVariable}
            />
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
} 